import { getMediaSourceUrl } from '../utils/media'
import type { IProjectAuthorShort, IProjectTeamMemberResource, IProjectTeamPreviewItem } from './types/projects'
import type { ITeamViewerPermissions } from './types/teams'
import type { TeamDescription } from '../types/entitys/teams'

export type TeamFormApiPayload = {
  title: string
  status: string
  city: string
  region: string
  descriptionShort: string
  descriptionFull?: string
  directions?: string[]
  goals?: string[]
  resources?: string[]
  needs?: string[]
}

function normalizeUserShort(raw: unknown): IProjectAuthorShort | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const avatarRaw = record.avatar ?? record.avatar_url ?? record.avatarUrl
  const idRaw = record.id

  return {
    id: idRaw != null && idRaw !== '' ? Number(idRaw) : null,
    slug: record.slug != null ? String(record.slug) : null,
    shortname: record.shortname != null ? String(record.shortname) : null,
    fullname: record.fullname != null ? String(record.fullname) : null,
    avatar: getMediaSourceUrl(avatarRaw),
    is_online: record.is_online === true,
    deleted: record.deleted === true,
  }
}

function parseTeamViewerPermissions(viewer: unknown): ITeamViewerPermissions | undefined {
  if (!viewer || typeof viewer !== 'object') return undefined
  const permissions = (viewer as Record<string, unknown>).permissions
  if (!permissions || typeof permissions !== 'object') return undefined
  const row = permissions as Record<string, unknown>

  return {
    is_owner: row.is_owner === true,
    can_edit: row.can_edit === true,
    can_edit_content: row.can_edit_content === true || row.can_edit === true,
    can_manage_team: row.can_manage_team === true,
    can_manage_admins: row.can_manage_admins === true,
    can_respond_invitations: row.can_respond_invitations === true,
    can_delete_team: row.can_delete_team === true,
  }
}

export function normalizeTeamMembersPreview(raw: unknown): IProjectTeamPreviewItem[] {
  const list = (raw as Record<string, unknown>)?.members_preview
  if (!Array.isArray(list)) return []

  return list
    .map((item: unknown) => {
      if (!item || typeof item !== 'object') return null
      const row = item as Record<string, unknown>
      const user = normalizeUserShort(row.user)
      if (!user) return null
      const teamRole = String(row.team_role ?? (row.is_author ? 'author' : 'member'))
      return {
        user,
        team_role: (teamRole === 'author' || teamRole === 'coauthor' || teamRole === 'member'
          ? teamRole
          : 'member') as IProjectTeamPreviewItem['team_role'],
        role_label: row.role_label != null ? String(row.role_label) : null,
        is_author: Boolean(row.is_author),
      } satisfies IProjectTeamPreviewItem
    })
    .filter((item): item is IProjectTeamPreviewItem => item !== null)
}

export function normalizeTeamMemberResource(item: unknown): IProjectTeamMemberResource | null {
  if (!item || typeof item !== 'object') return null
  const row = item as Record<string, unknown>
  const user = normalizeUserShort(row.user)
  if (!user) return null
  const rawTeamRole = String(row.team_role ?? 'member')
  const teamRole = rawTeamRole === 'author'
    ? 'author'
    : (rawTeamRole === 'coauthor' ? 'coauthor' : 'member')

  const fromRow = Number(row.user_id)
  const fromUser = typeof user.id === 'number' ? user.id : Number(user.id)
  const userId = Number.isFinite(fromRow) && fromRow > 0
    ? fromRow
    : (Number.isFinite(fromUser) && fromUser > 0 ? fromUser : 0)
  if (userId <= 0) return null

  return {
    user,
    user_id: userId,
    permission_role: row.permission_role === 'admin' ? 'admin' : 'member',
    team_role: teamRole,
    role_label: row.role_label != null ? String(row.role_label) : null,
    is_featured: Boolean(row.is_featured),
    sort_order: Number(row.sort_order ?? 0),
    is_author: Boolean(row.is_author),
    source_team: null,
  }
}

export function normalizeTeamItem(raw: Record<string, unknown>): TeamDescription {
  const about = (raw.about ?? {}) as Record<string, unknown>
  const counters = (raw.counters ?? {}) as Record<string, unknown>
  const viewerPermissions = parseTeamViewerPermissions(raw.viewer)

  return {
    id: Number(raw.id),
    slug: raw.slug != null ? String(raw.slug) : undefined,
    authorId: Number(raw.author_id ?? (raw.author as Record<string, unknown> | undefined)?.id ?? 0),
    title: String(raw.name ?? ''),
    city: String(raw.city ?? ''),
    status: String(raw.slogan ?? ''),
    descriptionShort: String(about.description ?? ''),
    descriptionFull: String(about.full_description ?? about.description ?? ''),
    region: String(raw.region ?? ''),
    directions: Array.isArray(raw.directions) ? (raw.directions as string[]) : [],
    goals: Array.isArray(raw.goals) ? (raw.goals as string[]) : [],
    projects: [],
    resources: Array.isArray(raw.resources) ? (raw.resources as string[]) : [],
    needs: Array.isArray(raw.needs) ? (raw.needs as string[]) : [],
    image: getMediaSourceUrl(raw.image) ?? '',
    participants: Number(counters.members ?? 0),
    subscribers: Number(counters.subscribers ?? 0),
    views: Number(counters.views ?? 0),
    linkedProjects: Number(counters.linked_projects ?? 0),
    isSubscribed: Boolean(raw.is_subscribed ?? (raw.viewer as Record<string, unknown> | undefined)?.is_subscribed),
    isJoined: Boolean(raw.is_joined ?? (raw.viewer as Record<string, unknown> | undefined)?.is_joined),
    hasPendingJoinRequest: Boolean(
      raw.has_pending_join_request
        ?? (raw.viewer as Record<string, unknown> | undefined)?.has_pending_join_request,
    ),
    isOwner: viewerPermissions?.is_owner ?? false,
    canEditContent: viewerPermissions?.can_edit_content ?? false,
    canManageTeam: viewerPermissions?.can_manage_team ?? false,
    canRespondInvitations: viewerPermissions?.can_respond_invitations ?? false,
    canDeleteTeam: viewerPermissions?.can_delete_team ?? false,
    viewerPermissions,
    publicationStatus: raw.status != null ? String(raw.status) : undefined,
    kind: raw.kind != null ? String(raw.kind) : undefined,
    membersPreview: normalizeTeamMembersPreview(raw),
  }
}

export function mapTeamFormToApiBody(payload: TeamFormApiPayload) {
  return {
    name: payload.title,
    slogan: payload.status,
    city: payload.city,
    region: payload.region,
    description: payload.descriptionShort,
    full_description: payload.descriptionFull || undefined,
    directions: payload.directions,
    goals: payload.goals,
    resources: payload.resources,
    needs: payload.needs,
  }
}
